import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { WaveProgressProps } from './WaveProgress.types'
import { WAVE_SVG_PATH, WAVE_VIEW_BOX, WAVE_VIEW_WIDTH, WAVE_VIEW_HEIGHT } from './waveSvgPath'
import './WaveProgress.scss'

// Path params used to analytically place the marker on the wave. Mirrors the
// SVG in waveSvgPath.ts — if that path ever changes shape, sync these.
//
// The path is a sequence of cubic Bezier "bumps", each 16 viewBox-units wide.
// Up-bumps have control points at y=-0.47 (below midline); down-bumps at
// y=8.07. Since the four control points of each segment are evenly spaced
// in x (Δx ≈ 5.33), x(t) is linear in t — which lets us evaluate y(t)
// cheaply without inverting Bezier x.
const PATH_START_X = 0.6          // first control-point x
const PATH_MIDLINE = 3.8          // viewBox-y of the wave midline
const PATH_SEGMENT_WIDTH = 16     // one half-cycle (up or down bump)
const PATH_PERIOD = 2 * PATH_SEGMENT_WIDTH  // full cycle = 32
const PATH_CONTROL_OFFSET = 4.27  // |controlY − midline| (3.8 − (−0.47))

const EASE_DURATION_MS = 320

/**
 * Evaluate the wave's viewBox-y at a given viewBox-x.
 *
 * Per cubic Bezier segment with control ys (y0, y1, y2, y3) = (mid, off, off,
 * mid), the symbolic simplification is `y = mid + 3·t·(1−t)·(off − mid)`
 * (derived from the Bernstein expansion using u³+v³−1 = −3·u·v with u+v=1).
 * The sign of `(off − mid)` alternates per segment.
 */
function waveY(viewBoxX: number): number {
  const shifted = viewBoxX - PATH_START_X
  let p = shifted % PATH_PERIOD
  if (p < 0) p += PATH_PERIOD
  const segInPeriod = p < PATH_SEGMENT_WIDTH ? 0 : 1
  const t = (p - segInPeriod * PATH_SEGMENT_WIDTH) / PATH_SEGMENT_WIDTH
  const sign = segInPeriod === 0 ? -1 : 1
  return PATH_MIDLINE + 3 * t * (1 - t) * sign * PATH_CONTROL_OFFSET
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

/**
 * Sinusoidal progress bar.
 *
 * Two overlaid wave strokes — a muted background (full width) and a
 * brand-violet foreground that clips from the left to `value * 100%`. An
 * optional marker node can be positioned at the tip of the fill; the marker
 * follows the wave up and down along the path instead of sitting on a flat
 * horizontal line.
 *
 * Animation: the displayed value is driven by a rAF loop that eases between
 * the previous and the new prop value over `EASE_DURATION_MS`. Every frame we
 * re-derive both `left` and `top` from the current eased value, so the marker
 * traces the curve instead of lerping A→B along a straight line the way a
 * plain CSS `transition: left, top` would.
 *
 * @example
 * // Pass any React node as `marker` to ride the wave tip. Currently unused
 * // app-wide — previously shown in ClarifyHeader with a 20 px SmileyIcon.
 * // To re-enable, import the icon and forward it to the `marker` prop:
 * //   import { SmileyIcon } from '@/src/renderer/components/icons/SmileyIcon'
 * //   <WaveProgress value={progress} marker={<SmileyIcon size={20} />} />
 * <WaveProgress value={0.6} marker={<SmileyIcon size={20} />} />
 */
export const WaveProgress = ({
  value,
  marker,
  height = 8,
  className,
}: WaveProgressProps) => {
  // Measure the rendered container so we can derive the SVG's effective scale
  // (`preserveAspectRatio="xMinYMid slice"` uses max(w/W, h/H)) and map the
  // marker's fill-x back into viewBox-x — without that, the marker sits on a
  // flat midline regardless of where the wave actually is at that x.
  const rootRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })
  useLayoutEffect(() => {
    const el = rootRef.current
    if (!el) return
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight })
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // rAF-driven displayed value. Stored in a ref (no render-per-frame cost for
  // holding it), but each frame we tick a dummy state to re-render with the
  // fresh `displayedValueRef.current`.
  const displayedValueRef = useRef(value)
  const [, forceRender] = useState(0)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    const from = displayedValueRef.current
    const to = value
    if (Math.abs(from - to) < 1e-4) {
      displayedValueRef.current = to
      return
    }
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / EASE_DURATION_MS)
      const eased = easeInOutCubic(t)
      displayedValueRef.current = from + (to - from) * eased
      forceRender(n => n + 1)
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        rafRef.current = null
      }
    }
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [value])

  const displayed = Math.max(0, Math.min(1, displayedValueRef.current))
  const fillPercent = displayed * 100

  // Resolve the marker's vertical offset from the container's centreline by
  // sampling the cubic-Bezier path at the marker's x. Until the first
  // measurement lands, fall back to the SCSS default (`top: 50%`).
  const markerStyle: React.CSSProperties = { left: `${fillPercent}%` }
  if (size.w > 0 && size.h > 0) {
    const scale = Math.max(size.w / WAVE_VIEW_WIDTH, size.h / WAVE_VIEW_HEIGHT)
    const containerX = displayed * size.w
    const viewBoxX = containerX / scale
    const viewBoxY = waveY(viewBoxX)
    const markerY = size.h / 2 + (viewBoxY - WAVE_VIEW_HEIGHT / 2) * scale
    markerStyle.top = `${markerY}px`
  }

  return (
    <div
      ref={rootRef}
      className={`wave-progress${className ? ` ${className}` : ''}`}
      style={{ height }}
      role="progressbar"
      aria-valuenow={Math.round(fillPercent)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <svg
        className="wave-progress__bg"
        viewBox={WAVE_VIEW_BOX}
        preserveAspectRatio="xMinYMid slice"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d={WAVE_SVG_PATH}
          stroke="var(--wave-progress-bg, var(--colors-stone-200, #e7e5e4))"
          strokeWidth={1.2}
          strokeLinecap="round"
        />
      </svg>
      <div
        className="wave-progress__fg-clip"
        style={{ width: `${fillPercent}%` }}
      >
        <svg
          className="wave-progress__fg"
          viewBox={WAVE_VIEW_BOX}
          preserveAspectRatio="xMinYMid slice"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d={WAVE_SVG_PATH}
            stroke="var(--wave-progress-fg, var(--space-500, #6D28D9))"
            strokeWidth={1.4}
            strokeLinecap="round"
          />
        </svg>
      </div>
      {marker && (
        <div className="wave-progress__marker" style={markerStyle}>
          {marker}
        </div>
      )}
    </div>
  )
}
